import {
  Container,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select
} from '@mui/material';

import { AllowanceValuesType } from 'components/Allowance/AllowanceDetailForm';
import { Checkbox } from 'components/Checkbox';
import { LoadingAnimation } from 'components/LoadingAnimation';
import { FormikProps } from 'formik';
import { Location } from 'services/api';

import { AllowancePaddedPaper as PaddedPaper } from './AllowancePaddedPaper';

export const SelectAddresses = ({
  values,
  setFieldValue,
  touched,
  errors,
  locations,
  loading
}: FormikProps<AllowanceValuesType> & {
  locations: Location[];
  loading: boolean;
}) => {
  const allLocationsSelected =
    values.applicable_company_locations?.length === locations?.length;

  return (
    <Grid item>
      <PaddedPaper title={'Select addresses *'} disabled={!values.editable}>
        {loading ? (
          <Grid item xs={12}>
            <LoadingAnimation />
          </Grid>
        ) : (
          <Grid container item spacing={2}>
            <Grid item xs>
              <FormControl
                fullWidth
                error={
                  touched.applicable_company_locations &&
                  Boolean(errors.applicable_company_locations)
                }
                disabled={!values.editable}
              >
                {values.editable ? (
                  <InputLabel
                    id="select-addresses-label"
                    sx={{
                      '&.MuiInputLabel-shrink': {
                        color: '#E21B70'
                      }
                    }}
                  >
                    {'Select addresses'}
                  </InputLabel>
                ) : null}

                <Select
                  labelId="select-addresses-label"
                  multiple
                  value={values.applicable_company_locations}
                  onChange={(e) => {
                    e.target.value.length &&
                    (e.target.value as number[]).includes(-1)
                      ? setFieldValue(
                          'applicable_company_locations',
                          allLocationsSelected
                            ? []
                            : locations?.map((d) => d.id)
                        )
                      : setFieldValue(
                          'applicable_company_locations',
                          e.target.value
                        );
                  }}
                  input={
                    values.editable ? (
                      <OutlinedInput
                        label={'Select addresses'}
                        inputProps={{ 'aria-label': 'select-addresses' }}
                      />
                    ) : undefined
                  }
                  renderValue={() =>
                    allLocationsSelected
                      ? `All addresses selected (${values.applicable_company_locations.length})`
                      : `${values.applicable_company_locations.length} addresses selected`
                  }
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E21B70'
                    }
                  }}
                >
                  <MenuItem
                    key={-1}
                    value={-1}
                    sx={{ borderBottom: '1px solid #EBEBEB' }}
                  >
                    <Checkbox checked={allLocationsSelected} />
                    <ListItemText primary={'All addresses'} />
                  </MenuItem>
                  {locations?.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      <Checkbox
                        checked={values.applicable_company_locations.includes(
                          location.id
                        )}
                      />
                      <ListItemText primary={location.campus} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {touched.applicable_company_locations
                    ? errors.applicable_company_locations
                    : ''}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Checkbox
                variant="primary"
                label="Allow employees to select their own addresses"
                checked={values.allow_flexible_locations || false}
                onChange={(e, checked) => {
                  setFieldValue('allow_flexible_locations', checked);
                }}
                disabled={!values.editable}
              />
              <Container
                sx={{
                  fontWeight: 300,
                  fontSize: 14,
                  color: '#33333',
                  ml: 3,
                  lineHeight: 1.5
                }}
              >
                Employees are able to use this allowance at the company
                addresses selected above and addresses added by themselves
              </Container>
            </Grid>
          </Grid>
        )}
      </PaddedPaper>
    </Grid>
  );
};
